<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import {
  branchesAPI,
  branchBrandsAPI,
  branchCitiesAPI,
} from 'dashboard/api/branches';
import BranchEditForm from './BranchEditForm.vue';

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const branches = ref([]);
const brands = ref([]);
const cities = ref([]);
const loading = ref(false);
const expandedId = ref(null);
const savingId = ref(null);
const geocodingId = ref(null);
const edits = reactive({});

// Hide deactivated branches by default (e.g. the old ones left over after a source-field switch).
const showInactive = ref(false);
const visibleBranches = computed(() =>
  showInactive.value ? branches.value : branches.value.filter(b => b.is_active)
);
const inactiveCount = computed(
  () => branches.value.filter(b => !b.is_active).length
);

const VIEW_KEY = 'cw_branches_view_mode';
const viewMode = ref(
  localStorage.getItem(VIEW_KEY) === 'card' ? 'card' : 'table'
);
const setView = mode => {
  viewMode.value = mode;
  localStorage.setItem(VIEW_KEY, mode);
};

const sortByName = list => list.sort((a, b) => a.name.localeCompare(b.name));

const normalizeSchedule = schedule => {
  const s = schedule && typeof schedule === 'object' ? schedule : {};
  const days = {};
  DAYS.forEach(key => {
    const d = (s.days && s.days[key]) || {};
    const intervals =
      Array.isArray(d.intervals) && d.intervals.length
        ? d.intervals.map(iv => ({ from: iv[0] || '09:00', to: iv[1] || '18:00' }))
        : [{ from: '09:00', to: '18:00' }];
    days[key] = { open: d.open !== undefined ? !!d.open : true, intervals };
  });
  return { tz: s.tz || 'Europe/Moscow', always_open: !!s.always_open, days };
};

const serializeSchedule = edit => {
  const days = {};
  DAYS.forEach(key => {
    const d = edit.days[key];
    days[key] = {
      open: d.open,
      intervals: d.open ? d.intervals.map(iv => [iv.from, iv.to]) : [],
    };
  });
  return { tz: edit.tz, always_open: edit.always_open, days };
};

const scheduleSummary = branch => {
  const s = branch.schedule || {};
  if (s.always_open) return 'Круглосуточно';
  if (!s.days) return '—';
  const open = DAYS.filter(k => s.days[k] && s.days[k].open).length;
  return `${open}/7 дней`;
};

const fetchAll = async () => {
  loading.value = true;
  try {
    const [b, br, c] = await Promise.all([
      branchesAPI.get(),
      branchBrandsAPI.get(),
      branchCitiesAPI.get(),
    ]);
    branches.value = b.data;
    brands.value = sortByName(br.data);
    cities.value = sortByName(c.data);
  } catch (error) {
    useAlert('Не удалось загрузить филиалы');
  } finally {
    loading.value = false;
  }
};
onMounted(fetchAll);

const toggle = branch => {
  if (expandedId.value === branch.id) {
    expandedId.value = null;
    return;
  }
  edits[branch.id] = {
    city_id: branch.city_id,
    brand_id: branch.brand_id,
    address: branch.address || '',
    latitude: branch.latitude,
    longitude: branch.longitude,
    is_active: branch.is_active,
    schedule: normalizeSchedule(branch.schedule),
  };
  expandedId.value = branch.id;
};

const addBrand = async branchId => {
  const name = window.prompt('Название бренда');
  if (!name || !name.trim()) return;
  try {
    const { data } = await branchBrandsAPI.create({ name: name.trim() });
    brands.value = sortByName([...brands.value, data]);
    edits[branchId].brand_id = data.id;
  } catch (error) {
    useAlert('Не удалось добавить бренд');
  }
};

const addCity = async branchId => {
  const name = window.prompt('Название города');
  if (!name || !name.trim()) return;
  try {
    const { data } = await branchCitiesAPI.create({ name: name.trim() });
    cities.value = sortByName([...cities.value, data]);
    edits[branchId].city_id = data.id;
  } catch (error) {
    useAlert('Не удалось добавить город');
  }
};

const geocode = async branch => {
  const edit = edits[branch.id];
  if (!edit.address) {
    useAlert('Сначала введите адрес');
    return;
  }
  geocodingId.value = branch.id;
  try {
    const { data } = await branchesAPI.geocode(edit.address);
    if (data.found) {
      edit.latitude = data.latitude;
      edit.longitude = data.longitude;
      useAlert('Координаты определены');
    } else {
      useAlert('Адрес не найден');
    }
  } catch (error) {
    useAlert('Ошибка геокодинга');
  } finally {
    geocodingId.value = null;
  }
};

const save = async branch => {
  const edit = edits[branch.id];
  savingId.value = branch.id;
  try {
    const { data } = await branchesAPI.update(branch.id, {
      branch: {
        city_id: edit.city_id,
        brand_id: edit.brand_id,
        address: edit.address,
        latitude: edit.latitude,
        longitude: edit.longitude,
        is_active: edit.is_active,
        schedule: serializeSchedule(edit.schedule),
      },
    });
    const idx = branches.value.findIndex(x => x.id === branch.id);
    if (idx !== -1) branches.value[idx] = data;
    useAlert('Сохранено');
    expandedId.value = null;
  } catch (error) {
    useAlert('Ошибка сохранения');
  } finally {
    savingId.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1">
    <div class="w-full max-w-6xl px-6 py-6 mx-auto">
      <div class="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 class="mb-1 text-xl font-medium text-n-slate-12">Филиалы</h1>
          <p class="text-sm text-n-slate-11">
            Названия из Bitrix24. Город, бренд, адрес, координаты и время работы
            заполняются здесь — синхронизация их не перезаписывает.
          </p>
        </div>
        <div v-if="branches.length" class="flex items-center gap-1 shrink-0">
          <button
            type="button"
            class="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak transition-colors"
            :class="
              viewMode === 'card'
                ? 'bg-n-slate-3 text-n-slate-12 dark:bg-n-solid-3'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="setView('card')"
          >
            <span class="i-ph-squares-four size-4" />
            Карточки
          </button>
          <button
            type="button"
            class="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak transition-colors"
            :class="
              viewMode === 'table'
                ? 'bg-n-slate-3 text-n-slate-12 dark:bg-n-solid-3'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="setView('table')"
          >
            <span class="i-ph-table size-4" />
            Таблица
          </button>
          <button
            v-if="inactiveCount"
            type="button"
            class="px-2.5 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
            @click="showInactive = !showInactive"
          >
            {{ showInactive ? 'Скрыть выключенные' : `Показать выключенные (${inactiveCount})` }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="py-10 text-center text-n-slate-11">
        Загрузка…
      </div>
      <div
        v-else-if="!branches.length"
        class="py-10 text-center text-n-slate-11"
      >
        Филиалов нет. Настройте источник филиалов в интеграции Bitrix24.
      </div>

      <!-- CARD VIEW -->
      <div v-else-if="viewMode === 'card'" class="flex flex-col gap-3">
        <div
          v-for="branch in visibleBranches"
          :key="branch.id"
          class="border rounded-xl border-n-weak bg-n-solid-1"
        >
          <button
            type="button"
            class="flex items-center w-full gap-3 px-4 py-3 text-left"
            @click="toggle(branch)"
          >
            <span class="i-lucide-building-2 size-5 text-n-slate-10 shrink-0" />
            <div class="flex-1 min-w-0">
              <div class="font-medium truncate text-n-slate-12">
                {{ branch.name }}
                <span v-if="!branch.is_active" class="ml-2 text-xs text-n-ruby-9">
                  выключен
                </span>
              </div>
              <div class="text-xs truncate text-n-slate-10">
                {{ branch.brand_name || 'без бренда' }} ·
                {{ branch.city_name || 'без города' }} ·
                {{ scheduleSummary(branch) }} · {{ branch.members_count }} опер.
              </div>
            </div>
            <span
              class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform"
              :class="{ 'rotate-180': expandedId === branch.id }"
            />
          </button>
          <div
            v-if="expandedId === branch.id"
            class="px-4 pt-3 pb-4 border-t border-n-weak"
          >
            <BranchEditForm
              :edit="edits[branch.id]"
              :brands="brands"
              :cities="cities"
              :geocoding="geocodingId === branch.id"
              :saving="savingId === branch.id"
              @save="save(branch)"
              @geocode="geocode(branch)"
              @add-brand="addBrand(branch.id)"
              @add-city="addCity(branch.id)"
            />
          </div>
        </div>
      </div>

      <!-- TABLE VIEW -->
      <div
        v-else
        class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1"
      >
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-n-weak">
              <th class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap">Филиал</th>
              <th class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap">Бренд</th>
              <th class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap">Город</th>
              <th class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap">Адрес</th>
              <th class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap">Время работы</th>
              <th class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap">Опер.</th>
              <th class="px-3 py-2.5 font-medium text-left text-n-slate-11 whitespace-nowrap">Статус</th>
              <th class="w-8 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            <template v-for="branch in visibleBranches" :key="branch.id">
              <tr
                class="border-b cursor-pointer border-n-weak last:border-0 hover:bg-n-alpha-1"
                @click="toggle(branch)"
              >
                <td class="px-3 py-2.5 font-medium text-n-slate-12 whitespace-nowrap">
                  {{ branch.name }}
                </td>
                <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">
                  <span v-if="branch.brand_name">{{ branch.brand_name }}</span>
                  <span v-else class="text-n-slate-10">—</span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">
                  <span v-if="branch.city_name">{{ branch.city_name }}</span>
                  <span v-else class="text-n-slate-10">—</span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-11 max-w-xs truncate">
                  <span v-if="branch.address">{{ branch.address }}</span>
                  <span v-else class="text-n-slate-10">—</span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">
                  {{ scheduleSummary(branch) }}
                </td>
                <td class="px-3 py-2.5 text-n-slate-11 tabular-nums">
                  {{ branch.members_count }}
                </td>
                <td class="px-3 py-2.5 whitespace-nowrap">
                  <span v-if="branch.is_active" class="text-n-teal-11">активен</span>
                  <span v-else class="text-n-ruby-9">выключен</span>
                </td>
                <td class="px-3 py-2.5 text-right">
                  <span
                    class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform"
                    :class="{ 'rotate-180': expandedId === branch.id }"
                  />
                </td>
              </tr>
              <tr v-if="expandedId === branch.id" class="bg-n-alpha-1">
                <td colspan="8" class="px-4 py-4">
                  <BranchEditForm
                    :edit="edits[branch.id]"
                    :brands="brands"
                    :cities="cities"
                    :geocoding="geocodingId === branch.id"
                    :saving="savingId === branch.id"
                    @save="save(branch)"
                    @geocode="geocode(branch)"
                    @add-brand="addBrand(branch.id)"
                    @add-city="addCity(branch.id)"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
